import api from './api';

/** PRX-51: what satisfies an item at product verification. Defaults to 'scan'. */
export type ProductVerificationEvidenceMode = 'scan' | 'photo';

export interface PharmacyFeatures {
  shipstationEnabled: boolean;
  descartesEnabled: boolean;
  /** PRX-24: Clinic / IV module visible for this pharmacy (nav section + dispensing-pharmacy field). Defaults to true. */
  clinicModuleEnabled: boolean;
  /** PRX-16: one Shipping page replaces Ready to Ship and Staging. Defaults to false. */
  simpleShippingEnabled: boolean;
  /** PRX-51: 'scan' (today, the default) or 'photo' (a webcam photo of the filled product also counts). */
  productVerificationEvidence: ProductVerificationEvidenceMode;
}

export interface PharmacyFeatureAuditEntry {
  id: string;
  featureKey: string;
  oldValue: boolean | null;
  newValue: boolean | null;
  /** PRX-51: set instead of oldValue / newValue for a non-boolean flag. */
  oldValueText: string | null;
  newValueText: string | null;
  changedByUserId: string | null;
  changedByName: string | null;
  createdAt: string;
}

export interface PharmacyFeaturesResponse {
  pharmacyId: string;
  features: PharmacyFeatures;
  hasShipStationStoreMapping: boolean;
  shipstationStoreId?: number;
  /**
   * ShipStation user ("Assigned To") that this pharmacy's pushed orders are
   * assigned to, so ShipStation automation rules can be scoped to one pharmacy.
   * Undefined = orders are pushed unassigned.
   */
  shipstationAssignedUser?: string;
  recentChanges: PharmacyFeatureAuditEntry[];
}

export interface UpdatePharmacyFeaturesRequest {
  shipstationEnabled?: boolean;
  descartesEnabled?: boolean;
  clinicModuleEnabled?: boolean;
  simpleShippingEnabled?: boolean;
  productVerificationEvidence?: ProductVerificationEvidenceMode;
}

const baseUrl = '/api/pharmacies';

export const pharmacyFeaturesApi = {
  async get(pharmacyId: string): Promise<PharmacyFeaturesResponse> {
    const res = await api.get<PharmacyFeaturesResponse>(
      `${baseUrl}/${pharmacyId}/features`,
    );
    return res.data;
  },

  async update(
    pharmacyId: string,
    body: UpdatePharmacyFeaturesRequest,
  ): Promise<PharmacyFeaturesResponse> {
    const res = await api.patch<PharmacyFeaturesResponse>(
      `${baseUrl}/${pharmacyId}/features`,
      body,
      { headers: { 'x-pharmacy-id': pharmacyId } },
    );
    return res.data;
  },
};

export const pharmacyFeaturesQueryKey = (pharmacyId: string) =>
  ['pharmacyFeatures', pharmacyId] as const;
